// Copyright (c) 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from "react";

import isEqual from 'lodash/isEqual';

import {
  ActionEventArgs,
  ColumnChooser,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  GridComponent,
  Group,
  Resize,
  Sort,
  Reorder,
  Edit,
  Inject,
  Toolbar,
  Freeze,
  ColumnModel,
  DetailRow
} from "@syncfusion/ej2-react-grids";

import { Tooltip } from '@syncfusion/ej2-popups'; 
import { SHEETS_HEADINGS_HEIGHT, WA_SUMMARY_GRID_COLLAPSED_ITEMS, GRID_WA_SUMMARY_COLUMN_HEADING_MAP, GROUPING_WA_SUMMARY } from "@shared/constants/sp-sheets";
import { withStyles } from "tss-react/mui";
import { MultiSelect, CheckBoxSelection, ComboBox } from "@syncfusion/ej2-react-dropdowns";
import { feetPerM, readFrLocalStorage } from "@shared/utils/common";
import SBase, { restoreCollapsedItems, saveCollapsedItems } from "../../SPContainer/components/SPSheet/components/SBase";
import { ROLES } from "@shared/constants/user";
import { ASSET_CONSTRUCTION_TYPE_LABELS } from "@shared/constants/asset";
import { WASummary } from "@shared/models/windarea";
import { NotificationType, showNotification } from "@shared/components/Notification";

MultiSelect.Inject(CheckBoxSelection);

// prop interface for the main class.
export interface SSummaryProps {
  className?: string;
  name: string;                                // Order Id.
  summaryInfo: () => Array<WASummary>;         // Summary info
  checkEquipmentChanged: (boolean) => boolean; // Flag indicating the equipment array has changed
}

/*
  Display Lattice tower panels using syncfusion grid.
  Doc: https://ej2.syncfusion.com/documentation/grid/getting-started/
*/
class SSummary extends SBase<SSummaryProps, {grouping: string}> {
  private mainData: Array<WASummary>;
  private isCustomerUser: boolean;
  private headingMapObj: any;
  private gridGroupingStr: string;
  private gridCollapsedItemsStr: string;

  private unitM: boolean; // true: mm, false: inch

  // private dlgTitle: string;
  // private dlgMessage: string;
  // private toBeDeletedIds: Array<number>;
  // private dlgItems: Array<string>;

  // Var to setup selection box in panel type cell.
  private equipmentComboElem: HTMLElement | undefined;
  private equipmentCombo: ComboBox | undefined;             // Handle to comboBox to select a panel type.

  constructor(props: SSummaryProps) {
    super(props);
    this.setupCopyAndPaste();
    this.headingMapObj = GRID_WA_SUMMARY_COLUMN_HEADING_MAP;
    this.gridGroupingStr = GROUPING_WA_SUMMARY;
    this.gridCollapsedItemsStr = WA_SUMMARY_GRID_COLLAPSED_ITEMS;

    this.setupColumnNCollapse(["50px", "150px", "100px", "90px", "90px", "120px", "100px", "110px", "80px", "80px", "80px"],
                             this.headingMapObj, this.gridCollapsedItemsStr);
    
    this.isCustomerUser = this.authStore.user.roles.every((role) => ROLES.customerUser == role);

    this.state = {grouping: readFrLocalStorage(this.mkUserKey(this.gridGroupingStr))};

    // this.groupOptions = { columns: [], allowReordering: true, captionTemplate: this.myGroupCaptionTemplate.bind(this) }; // TBD: grouping.
    this.groupOptions = { columns: [], allowReordering: true };

    this.mainData = [];

    // this.dlgTitle = '';
    // this.dlgMessage = '';
    // this.dlgItems = [];

    // this.toBeDeletedIds = [];

    this.unitM = true;
    this.toolbar.push({ text: 'Unit: m', prefixIcon: 'e-change-scale-ratio', id: 'unit', tooltipText: 'Change the measurement unit (m <=> ft)' });
  }

  // Translate construction name to the corresponding type Id.
  constructionName2type = (n: string | undefined): number => {
    for(const propId in ASSET_CONSTRUCTION_TYPE_LABELS) {
      if(ASSET_CONSTRUCTION_TYPE_LABELS[propId] === n) {
        return Number(propId);
      }
    }
    return 0;
  }

  // TBD.
  myGroupCaptionTemplate(args) {
    //console.log('***** (SSummary) myGroupCaptionTemplate:', args, args.items.childLevels);

    // let recs: Array<AssetVisitInfoDTO>;
    const key = '<TBD>';

    return (
      <div>{key}</div>
    );
  }

  initializeMainData() {
    const { summaryInfo } = this.props;

    // console.log('*** (SSummary) initialize mainData.');

    this.mainData.length = 0;

    summaryInfo().forEach(summary => {
      this.mainData.push({...summary});
    });
    // console.log("*** (SSummary) mainData", this.mainData);
  }

  // Triggered after the component loaded.
  // Load panelsInfo from portal-api.
  // Note: Calls that need 'await' should be here.
  async componentDidMount(): Promise<void> {
    super.componentDidMount();
    this.setState((state) => ({ ...state, loading: true }));

    this.initializeMainData();

    //console.log("*** (SSummary) clear loading to trigger a rerender.");
    this.setState((state) => ({ ...state, loading: false }));
  }

  // Triggered when a row is selected for editing.
  public async handleActionBegin(args: any) {
    // console.log('*** (SSummary) actionBegin:', args)
    super.handleActionBegin(args, this.gridCollapsedItemsStr);
    if (args.name === 'actionBegin' && args.requestType === 'refresh') { // Save scroll position before refresh (does this still work?)
      const scrollTop = this.grid?.element.querySelectorAll(".e-content")[0].scrollTop;
      if (scrollTop) {
        this.lastVScrollPosition = scrollTop;
      }
    }
  }

  // makeMetric(arg: WASummary): WASummary {
  //   if (this.unitM) {
  //     return arg;
  //   }
  //   const metricObj = arg;
  //   metricObj.buildingHeight = arg.buildingHeight / feetPerM;
  //   metricObj.structureHeight = arg.structureHeight / feetPerM;
  //   metricObj.epaWindArea = arg.epaWindArea / feetPerM / feetPerM;
  //   return metricObj;
  // }

  // Triggered after cell(s) updated (ie. deselected a row after editing the cell(s)).
  // 'args' should have the request type, new and old cell values.
  public async handleActionComplete(args: ActionEventArgs) {
    // console.log('*** (SSummary) in actionComplete (SPanel):', args)

    const { requestType, type, data, action, previousData } = args;

    if(!this.isCustomerUser) {
      if(requestType === "save" && type === "actionComplete" && // check type and requestType
         !isEqual(data, previousData)) {                        // check if there are changes.
        if (action === 'add') {
          console.log('*** (SSummary) Add', data);
        }
        else if (action === 'edit') {
          console.log('*** (SSummary) Edit', data);
        }
        else {
          console.log("*** (SSummary) Unsupported action", action);
        }
      }
    }

    if (requestType === 'refresh') {
      const firstChild = this.grid?.getContent().children[0];
      if (firstChild) {
        firstChild.scrollTop = this.lastVScrollPosition;
      }
    }

    // For grouping event, store the new grouping in localStorage.
    if(args.requestType === "grouping" || args.requestType === "ungrouping") {
      if(this.grid?.groupSettings.columns) {
        const grouping = this.grid.groupSettings.columns.join(",");
        // console.log('****** (SSummary) store grouping', grouping);
        localStorage.setItem(this.mkUserKey(this.gridGroupingStr), grouping);
        //this.initialLoad = true;  // uncomment if want to collapse the rows.
      }
    }
  }
  
  handleHeaderCellInfo(args) {
    const valueArray = this.headingMapObj[ args.cell.column.field];
    if (valueArray != undefined)
    {
      const toolTip = valueArray['toolTip'];
      const tooltip: Tooltip = new Tooltip({ 
        content: toolTip,
        position: 'TopLeft',
        openDelay: 800
      }); 
      tooltip.appendTo(args.node);
    }
  }

  switchUnit() {
    // console.log('*** (SSummary) change input unit to', this.unitM ? 'm' : 'ft');

    // Modify flag
    this.unitM = !this.unitM;

    // Change toolbar item text
    let toolbarItemUnit = this.grid?.toolbarModule.getToolbar().querySelector('#unit');
    if (toolbarItemUnit && toolbarItemUnit.lastChild) {
      (toolbarItemUnit.lastChild as HTMLElement).innerText = this.unitM ? 'Unit: m' : 'Unit: ft';
    }

    // Convert grid's data
    const { summaryInfo } = this.props;
    this.mainData.forEach(item => {
      const summary = summaryInfo().find(x => x.towerId == item.towerId);
      if (summary) {
        item.buildingHeight = this.unitM ? summary.buildingHeight : summary.buildingHeight * feetPerM;
        item.structureHeight = this.unitM ? summary.structureHeight : summary.structureHeight * feetPerM;
        item.epaWindArea = this.unitM ? summary.epaWindArea : summary.epaWindArea * feetPerM * feetPerM;
      }
    });

    // Change the format of column 'Building height', 'Structure height', 'EPA wind Area'
    [8, 9, 10].forEach(col => {
      const colModel = this.grid?.columns[col] as ColumnModel;
      if (colModel) {
        colModel.format = this.unitM ? 'N3' : 'N2';
      }
    })
    this.grid?.refreshColumns();
    this.grid?.refresh();
  }

  async handleToolbarClicked(args): Promise<boolean> {
    // const { delaySync } = this.props;
    // delaySync(); // skip a sycle of autorefresh.

    const selectedTRowCellIndex = this.grid?.getSelectedRowCellIndexes();
    const currentViewData = super.getCurrentViewData();

    if(args.item.text === 'Delete') {
      // args.cancel = true; // Cancel the default delete action
    }
    else if (args.item.text === 'Add') {
    }
    else {
      const handled = await super.handleToolbarClicked(args);

      if(!handled && args.item.id === 'paste') {
        
      }
      else if (!handled && args.item.id === 'unit') {
        this.switchUnit();
      }
      else if (!handled && args.item.id === 'clone') {
        
      }
    }
    return true;
  }

  // Triggered after a cell being selected.
  handleCellSelected(args) {
    super.handleCellSelected(args);

    // const test = this.mainData.find(x =>)
  }

  componentWillUnmount() {
    super.componentWillUnmount()

    // Store collapsed items for persistence.
    this.collapsedItems = saveCollapsedItems(this.grid, this.mkUserKey(this.gridCollapsedItemsStr));
  }

  // Set cell background color based on exception code
  customizeCell(args) {
    // console.log('*** (SSummary) handleQueryCellInfo', args);
    // const item = args.data as AssetVisitInfoDTO & {site: string, typeName: string, deltaName: string};
    // if (args.cell && item?.deltaName) {
    //   const field = args.column.field;
    //   if (field == 'name') {
    //     args.cell.style.backgroundColor = DELIVERABLE_EXCEPTION_CODE_TO_COLOR['moderate'];
    //   }
    // }
  }

  handleLoad(args) {
    // console.log('*** (SSummary) handleLoad');
    const { checkEquipmentChanged } = this.props;
    if (checkEquipmentChanged(true)) {
      this.initializeMainData();

      // this.grid?.refreshColumns();
      // this.collapsedItems = saveCollapsedItems(this.grid, this.mkUserKey(this.gridCollapsedItemsStr));
      this.grid?.refresh();
      // restoreCollapsedItems(this.grid, this.collapsedItems);
    }
  }

  render() {
    const classes = withStyles.getClasses(this.props);
  
    // console.log("*** (SSummary) render order:", this.props.name);
    // console.log(">>> Data:", this.mainData);

    let columnIndex = 0;
    return (
      <div className={classes.root}>
        <GridComponent
          dataSource={this.mainData}
          ref={(g) => (this.grid = g)}
          id={this.mkUserKey('wind-area-summary')} // enablePersistence uses this as key to store the grid settings.
      
          // Basic configs
          editSettings={{ allowEditing: false }}
          selectionSettings={{ mode: 'Cell', type: 'Multiple', enableToggle: false }}
          toolbar={this.toolbar} 
          showColumnChooser={true}                // Allow showing/hiding cols
          gridLines='Both'                        // Show horizontal and vertical grid lines.
          height={Number(window.innerHeight - SHEETS_HEADINGS_HEIGHT)}

          // Event handlers. Bind 'this' so they can access props and state.
          //dataStateChange={this.handleDataStateChange.bind(this)}
          //dataSourceChanged={this.handleDataSourceChanged.bind(this)}
          dataBound={this.handleDataBound.bind(this)}

          actionBegin={this.handleActionBegin.bind(this)}
          actionComplete={this.handleActionComplete.bind(this)}
          headerCellInfo={this.handleHeaderCellInfo.bind(this)}
          toolbarClick={this.handleToolbarClicked.bind(this)}
          cellSelected={this.handleCellSelected.bind(this)}
          queryCellInfo={this.customizeCell.bind(this)}
          load={this.handleLoad.bind(this)}
          // handlePageActive={this.handlePageActive.bind(this)}

          //
          // Additional features.
          //

          // wrap header line if does not fit.
          allowTextWrap={true}
          textWrapSettings={{ wrapMode: 'Header' }} 

          // pagination.
          // allowPaging={true}  
          // pageSettings={{ pageSize: SHEETS_PAGE_SIZE }}

          // sorting rows.
          allowSorting={true}

          // Resize cols.
          allowResizing={true}

          // Reorder columns
          allowReordering={true}

          // Excel export
          // allowExcelExport={true}

          // Apparently, setting up grouping in one of the tabs will enable it in other tabs.  So must explicitly set to false if the feature is not used.
          allowGrouping={true}
          groupSettings={this.groupOptions}

          // filter rows.
          allowFiltering={true}
          filterSettings={{ type: 'CheckBox' }} // Note: it's possible to setup and show a 'filterTemplate' control for the filter.

          // Keep grid settings persistence.
          enablePersistence={true}
        >
          <ColumnsDirective> 
            <ColumnDirective // ID 0
              field={this.columnNames[columnIndex]}
              allowEditing={false}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              isPrimaryKey={true}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
            />
            { columnIndex++ }

            <ColumnDirective // Site 1
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              isPrimaryKey={false}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              // edit={this.BuildComboParams(this.equipmentComboElem, this.equipmentCombo, towerNames, this.columnNames[columnIndex])}
            />
            { columnIndex++ }

            <ColumnDirective // Region 2
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
            />
            { columnIndex++ }

            <ColumnDirective // Number of operators 3
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              type='Number'
            />
            { columnIndex++ }

            <ColumnDirective // Latitude 4
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              type='Number'
            />
            { columnIndex++ }

            <ColumnDirective // Longitude 5
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              type='Number'
            />
            { columnIndex++ }

            <ColumnDirective  // Site type 6
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}

              // edit={this.BuildComboParams(this.equipmentComboElem, this.equipmentCombo, possibleEquipmentTypes, this.columnNames[columnIndex])}
            />
            { columnIndex++ }

            <ColumnDirective  // Structure type 7
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              customAttributes={{ class: "customcss" }}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}

              // edit={this.BuildComboParams(this.equipmentComboElem, this.equipmentCombo, possibleEquipmentTypes, this.columnNames[columnIndex])}
            />
            { columnIndex++ }

            <ColumnDirective // Building height 8
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              type='Number'
              format='N3'
            />
            { columnIndex++ }

            <ColumnDirective // Structure height 9
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              type='Number'
              format='N3'
            />
            { columnIndex++ }

            <ColumnDirective // EPA Wind Area 10
              field={this.columnNames[columnIndex]}
              allowEditing={true}
              headerText={this.columnLabels[columnIndex]}
              width={this.initialWidths[columnIndex]}
              clipMode='EllipsisWithTooltip'
              visible={this.columnVisibility[columnIndex]}
              filter={{type: 'Excel'}}
              type='Number'
              format='N3'
            />
          </ColumnsDirective>
          <Inject services={[Freeze, Edit, Toolbar, Sort, Filter, ColumnChooser, Resize, Group, Reorder, DetailRow]} />
        </GridComponent>
      </div>
    )
  }
}

const SSummaryStyled = withStyles(
  SSummary, 
  (theme, props) => ({
    "root": {
      paddingTop: theme.spacing(5)
    },
  })
);

export default SSummaryStyled;